// entryhistory.jsx
import React, { useMemo, useState } from "react";
import axios from 'axios';
import { useQuery } from '@tanstack/react-query';
import "./entryhistory.css";

const API_URL = import.meta.env.VITE_API_URL;

export default function EntryHistory() {
  const [pageSize, setPageSize] = useState(50);
  const [page, setPage] = useState(0);

  // API からエントリー履歴を取得
  const {
    data: rows = [],
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ['entry-history'],
    queryFn: async () => {
      const res = await axios.get(`${API_URL}/api/entries/history/`);
      if (!res.data?.success) throw new Error('履歴の取得に失敗しました');
      return res.data.data;
    },
    staleTime: 30000,
  });

  const total = rows.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(page, totalPages - 1);

  const pageItems = useMemo(() => {
    const start = safePage * pageSize;
    return rows.slice(start, start + pageSize);
  }, [rows, safePage, pageSize]);

  const from = total === 0 ? 0 : safePage * pageSize + 1;
  const to = Math.min(total, (safePage + 1) * pageSize);

  const formatAmount = (n) => {
    const num = Number(n);
    if (Number.isNaN(num)) return n ?? "";
    return num.toLocaleString("ja-JP");
  };

  if (isLoading) {
    return (
      <div className="entryHistoryPage">
        <div className="entryHistoryCard">
          <div className="entryHistoryLoading">読み込み中...</div>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="entryHistoryPage">
        <div className="entryHistoryCard">
          <div className="entryHistoryError">
            エラー: {error?.message || '履歴の取得に失敗しました'}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="entryHistoryPage">
      <div className="entryHistoryCard">
        <div className="entryHistoryHeader">
          <div className="entryHistoryTitle">エントリー履歴</div>
          
          {/* ページネーション */}
          <div className="entryHistoryPagination">
            <span className="entryHistoryFooterLabel">表示件数:</span>
            <div className="entryHistorySelectWrap">
              <select
                className="entryHistorySelect"
                value={pageSize}
                onChange={(e) => {
                  const next = parseInt(e.target.value, 10) || 10;
                  setPageSize(next);
                  setPage(0);
                }}
              >
                {[5, 10, 25, 50, 100].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </div>
            <span className="entryHistoryRange">
              {from}-{to} / {total}
            </span>
            <div className="entryHistoryPagerButtons">
              <button
                type="button"
                className="entryHistoryPagerBtn"
                onClick={() => setPage(0)}
                disabled={safePage === 0}
                aria-label="First"
              >
                {"|<"}
              </button>
              <button
                type="button"
                className="entryHistoryPagerBtn"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={safePage === 0}
                aria-label="Prev"
              >
                {"<"}
              </button>
              <button
                type="button"
                className="entryHistoryPagerBtn"
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={safePage >= totalPages - 1}
                aria-label="Next"
              >
                {">"}
              </button>
              <button
                type="button"
                className="entryHistoryPagerBtn"
                onClick={() => setPage(totalPages - 1)}
                disabled={safePage >= totalPages - 1}
                aria-label="Last"
              >
                {">|"}
              </button>
            </div>
          </div>
        </div>

        <div className="entryHistoryTableWrap">
          <table className="entryHistoryTable">
            <thead>
              <tr>
                <th>決済日時</th>
                <th>イベント名</th>
                <th>開催日</th>
                <th>クラス名</th>
                <th>出陳犬名</th>
                <th className="is-right">金額</th>
              </tr>
            </thead>

            <tbody>
              {pageItems.length === 0 ? (
                <tr>
                  <td className="is-empty" colSpan={6}>
                    履歴がありません
                  </td>
                </tr>
              ) : (
                pageItems.map((r) => (
                  <tr key={r.id}>
                    <td className="col-paid" data-label="決済日時">{r.paid_at}</td>
                    <td className="col-event" data-label="イベント名" title={r.event_name}>
                      {r.event_name}
                    </td>
                    <td className="col-date" data-label="開催日">{r.event_date || ""}</td>
                    <td className="col-class" data-label="クラス名">{r.class_name}</td>
                    <td className="col-dog" data-label="出陳犬名" title={r.dog_name}>
                      {r.dog_name}
                    </td>
                    <td className="is-right col-amount" data-label="金額">{formatAmount(r.amount)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        
      </div>
    </div>
  );
}